import { message as antdMessage, Button, Form, type FormProps, Input, theme as themeAnt } from 'antd';
import type React from 'react';
import { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useNavigate } from 'react-router-dom';
import type {
  BasicUserInfo,
  DoubleCheckRequest,
  LoginRequest,
  LoginResponse,
  MessageResponse,
} from '@/app/Login/domain/Auth';
import { useLoginMutatio } from '@/auth/hooks/mutations/auth-mutations';
import { useUserIp } from '@/auth/hooks/use-user-ip';
import { useAuthStore } from '@/auth/store/auth.store';
import { useQueryClient } from '@tanstack/react-query';
import { buildApiUrl } from '@/global';
import type { BaseResponse } from '@/shared/common/interfaces';
import { usePost } from '@/shared/hook/usePost';
import AuthLayout from './AuthLayout';
import OtpForm from './OtpForm';

type FieldType = {
  username?: string;
  password?: string;
};

const SITE_KEY = '6LdbthQrAAAAANMIlIA3DZ86yAdfpoSdZoY7Un0_';
const USE_RECAPTCHA = true;

const Login: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [messageApi, contextHolder] = antdMessage.useMessage();
  const [needsOtp, setNeedsOtp] = useState(false);
  const [basicUserInfo, setBasicUserInfo] = useState<BasicUserInfo>({} as BasicUserInfo);
  const [privateUserInfo, setPrivateUserInfo] = useState<LoginRequest>({} as LoginRequest);

  const {
    token: { colorBgContainer },
  } = themeAnt.useToken();

  const { data: ipData } = useUserIp();
  const skipRecaptcha = ipData?.isTrusted ?? false;
  const userIp = ipData?.ip ?? '::1';
  const loginMutation = useLoginMutatio();

  const postLogin = usePost<unknown, BaseResponse<LoginResponse>>(buildApiUrl('/Cuenta/login'));
  const sendCodeEmail = usePost<DoubleCheckRequest, MessageResponse>(buildApiUrl('/Cuenta/generate-code'));

  const handlerGenerateCore = async (userInfo: BasicUserInfo) => {
    setBasicUserInfo(userInfo);
    const sendEmailResponse = await sendCodeEmail.postData({
      username: userInfo.username,
      email: userInfo.email,
    });

    if (sendEmailResponse?.result) {
      setNeedsOtp(true);
      messageApi.warning('Ingresa el código enviado a tu correo o app');
    } else {
      messageApi.error('No se ha podido enviar el código. Inténtelo nuevamente más tarde');
    }
  };

  const onFinishDoubleCheck = async (code: string) => {
    try {
      const payload = privateUserInfo;
      payload.code = code;

      const response = await postLogin.postData(payload);
      if (response?.data.resultado) {
        localStorage.setItem('username', payload.nombreUsuario || '');
        messageApi.success(response.data.mensaje);
        useAuthStore.getState().setLoadingMe(true);
        queryClient.invalidateQueries({ queryKey: ['auth-me'] });
        navigate('/');
      } else {
        messageApi.error(response?.data.mensaje || 'Usuario o contraseña incorrectos');
      }
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      messageApi.error('No se pudo conectar con el servidor. ' + errorMsg);
      return;
    }
  };

  const onFinish: FormProps<FieldType>['onFinish'] = async (values) => {
    let recaptchaToken = '';

    if (USE_RECAPTCHA && !skipRecaptcha) {
      try {
        recaptchaToken = (await recaptchaRef.current?.executeAsync()) || '';
        recaptchaRef.current?.reset();

        if (!recaptchaToken) {
          messageApi.error('No se pudo verificar el reCAPTCHA');
          return;
        }
      } catch (err: unknown) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        messageApi.error('Error en la validación de reCAPTCHA: ' + errorMsg);
        return;
      }
    }

    const payload: LoginRequest = {
      nombreUsuario: values.username,
      contrasena: values.password,
      direccionIP: userIp,
      recuerdame: true,
      recaptchaToken,
    };

    loginMutation.mutate(payload, {
      onSuccess: async (data) => {
        if (data.user?.doubleCheck) {
          setPrivateUserInfo(payload);
          await handlerGenerateCore(data.user);
          return;
        }
        localStorage.setItem('username', values.username || '');
        messageApi.success(data.mensaje);
        location.reload();
      },
    });
  };

  if (needsOtp) {
    return (
      <>
        {contextHolder}
        <OtpForm colorBgContainer={colorBgContainer} userInfo={basicUserInfo} onSubmit={onFinishDoubleCheck} />
      </>
    );
  }

  return (
    <AuthLayout title="Inicia sesión" hint="Bienvenido de vuelta. Ingresa a tu sala de control." showDemoCta>
      <Form
        name="basic"
        layout="vertical"
        initialValues={{
          remember: true,
          username: localStorage.getItem('username') ?? '',
        }}
        onFinish={onFinish}
        autoComplete="off"
      >
        <Form.Item<FieldType> label="Usuario" name="username" rules={[{ required: true }]}>
          <Input placeholder="Usuario" />
        </Form.Item>

        <Form.Item<FieldType> label="Contraseña" name="password" rules={[{ required: true }]}>
          <Input.Password placeholder="••••••••" />
        </Form.Item>

        <div className="row2">
          <a className="forgot" onClick={() => navigate('/recuperar')}>
            ¿Olvidaste tu contraseña?
          </a>
        </div>
        {/* Mostrar solo si no se omite */}
        {USE_RECAPTCHA && !skipRecaptcha && <ReCAPTCHA sitekey={SITE_KEY} size="invisible" ref={recaptchaRef} />}

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loginMutation.isPending} style={{ width: '100%' }}>
            Ingresar
          </Button>
        </Form.Item>
      </Form>
    </AuthLayout>
  );
};

export default Login;
